/**
 * useFriendData hook
 * Processes friends data: separates into REQUEST, FRIEND, PENDING types
 * Returns a unified array for simple FlashList rendering
 */

import { useMemo } from 'react'
import type { Friend } from '../types/friends.types'
import { buildListItems } from '../helpers/buildListItems.helper'

/**
 * Number of times to duplicate each friend for UI testing
 * Set to 1 to disable duplication (production)
 * @default 1
 */
export const TEST_DUPLICATE_COUNT = 1

export type FriendListItem = {
  type: 'REQUEST' | 'FRIEND' | 'PENDING'
  friend: Friend
  id: string
}

interface UseFriendDataProps {
  friends: Friend[]
  duplicateCount?: number
  /** @default 1 */
}

interface UseFriendDataReturn {
  items: FriendListItem[]
}

/**
 * Hook to process friends data for display
 * Separates friends into REQUEST (received), FRIEND (accepted), PENDING (sent)
 * Orders items: REQUEST → FRIEND → PENDING
 */
export const useFriendData = ({
  friends,
  duplicateCount = 1,
}: UseFriendDataProps): UseFriendDataReturn => {
  const items = useMemo(
    () =>
      buildListItems<Friend, FriendListItem>({
        items: friends,
        getCategory: (f) => {
          if (f.friendshipStatus === 'pending' && f.invitationType === 'received') return 'REQUEST'
          if (f.friendshipStatus === 'accepted') return 'FRIEND'
          if (f.friendshipStatus === 'pending' && f.invitationType === 'sent') return 'PENDING'
          return '__skip__'
        },
        categoryOrder: ['REQUEST', 'FRIEND', 'PENDING'],
        duplicateCount,
        getStableId: (f) => f.id,
        toListItem: (friend, type, listItemId, entityId, dupIndex, dupSubIndex) => ({
          type: type as FriendListItem['type'],
          friend:
            duplicateCount > 1 && dupIndex !== undefined && dupSubIndex !== undefined
              ? {
                  ...friend,
                  id: entityId,
                  invitationId: friend.invitationId
                    ? `${friend.invitationId}-dup-${dupIndex}-${dupSubIndex}`
                    : friend.invitationId,
                }
              : friend,
          id: listItemId,
        }),
      }),
    [friends, duplicateCount]
  )
  return { items }
}
